import { useEffect, useState, type ReactNode } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import axiosNest from '../api/axiosNest';
import { useFeature } from '../admin/featureFlags/FeatureGate';

/**
 * Pola obowiązkowe przyjęcia zlecenia na formularzach (prace 24.10.2026,
 * G1/K3; przełącznik `requiredFieldsHighlight`, S-24.10-03) i znacznik
 * pytania Administracji przy polu (G1/K4; `intakeQuestionField`,
 * S-24.10-04).
 *
 * **Front nie trzyma własnej listy** — czyta ją z API
 * (`GET /required-fields`, reguła `apps/api/src/required-fields/
 * required-fields.ts`, ta sama co walidator importera i lista braków
 * prezlecenia). Klucze to dane API: pole prezlecenia (`clientName`),
 * zleceniodawca (`orderingParty.nazwa`), objętość (`volume`), pole punktu
 * (`station.fromTime`). Zgodność kluczy z polami formularzy pilnuje
 * `requiredFields.test.ts` (czyta plik API).
 *
 * Wyróżnienie: etykieta półgruba (decyzja z paczki p. Mariusza 15.10:
 * „pola obligatoryjne wyróżnione innym krojem pisma”) i „(pole obowiązkowe)”
 * dla czytników ekranu. Stan „brak” przy pustym polu zostaje, jak był.
 */

export const FEATURE_REQUIRED_FIELDS = 'requiredFieldsHighlight';

export interface RequiredFieldDef {
  key: string;
  group: 'preOrder' | 'orderingParty' | 'shipment' | 'station';
  label: string;
}

let cache: Promise<RequiredFieldDef[]> | null = null;

/** Lista z API — raz na sesję; błąd = bez wyróżnienia (i ponowna próba później). */
export function fetchRequiredFields(): Promise<RequiredFieldDef[]> {
  if (!cache) {
    // `Promise.resolve().then` — także błąd rzucony od razu (klient HTTP niedostępny) kończy się listą pustą.
    cache = Promise.resolve()
      .then(() => axiosNest.get<{ fields: RequiredFieldDef[] }>('/required-fields'))
      .then(({ data }) => (Array.isArray(data?.fields) ? data.fields : []))
      .catch(() => {
        cache = null;
        return [];
      });
  }
  return cache;
}

/** Dla testów — czyści pamięć listy. */
export function resetRequiredFieldsCache(): void {
  cache = null;
}

/**
 * Czy pole jest obowiązkowe. Bez przełącznika albo przed odpowiedzią API —
 * nic nie jest wyróżnione (etykiety jak dotąd).
 */
export function useRequiredFields(): (key: string) => boolean {
  const on = useFeature(FEATURE_REQUIRED_FIELDS);
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (!on) return undefined;
    let alive = true;
    void fetchRequiredFields().then((fields) => {
      if (alive) setKeys(new Set(fields.map((f) => f.key)));
    });
    return () => {
      alive = false;
    };
  }, [on]);
  return (key: string) => on && keys.has(key);
}

/** Tekst tylko dla czytników ekranu. */
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/**
 * Etykieta pola: obowiązkowe — półgrubą czcionką z oznaczeniem dla czytników.
 *
 * `forInput` — etykieta pola formularza: bez dopisku w tekście (nazwa pola
 * zostaje ta sama, np. „Waga (kg)”), a czytnik dostaje „wymagane” z atrybutu
 * pola (`requiredInputAttrs`). Wiersze tabel (karta prezlecenia, okno
 * kontroli) nie mają pola — tam dopisek „(pole obowiązkowe)” dla czytników.
 */
export function fieldLabel(text: string, required: boolean, options: { forInput?: boolean } = {}): ReactNode {
  if (!required) return text;
  return (
    <Box component="span" sx={{ fontWeight: 600 }} data-required-field="true">
      {text}
      {options.forInput ? null : (
        <Box component="span" sx={VISUALLY_HIDDEN}>
          {' '}
          (pole obowiązkowe)
        </Box>
      )}
    </Box>
  );
}

/** Atrybut pola formularza dla czytników ekranu: pole obowiązkowe. */
export function requiredInputAttrs(required: boolean): { 'aria-required'?: true } {
  return required ? { 'aria-required': true } : {};
}

// ── Pytanie Administracji przypięte do pola (K4) ─────────────────────────

/** Otwarte pytanie z obiegu (`IntakeFlow.openQuestion`). */
export interface OpenQuestion {
  field: string | null;
  fieldLabel: string;
  message: string | null;
  authorName: string | null;
  createdAt: string;
}

/** Czy pytanie dotyczy któregoś z pól. */
export function questionAbout(question: OpenQuestion | null | undefined, keys: readonly string[]): boolean {
  return !!question && question.field !== null && keys.includes(question.field);
}

/**
 * Znacznik przy polu z otwartym pytaniem Administracji — treść w dymku
 * i w podpisie dla czytników. Znika po odpowiedzi CS (API nie zwraca wtedy
 * otwartego pytania).
 */
export const QuestionMarker = ({
  question,
  keys,
}: {
  question: OpenQuestion | null | undefined;
  keys: readonly string[];
}) => {
  if (!question || !questionAbout(question, keys)) return null;
  const text = `Pytanie Administracji: ${question.message ?? '—'}`;
  return (
    <Tooltip title={text}>
      <Chip
        size="small"
        color="warning"
        icon={<HelpOutlineIcon />}
        label="Pytanie"
        aria-label={text}
        sx={{ ml: 0.5 }}
        data-testid={`intake-question-marker-${question.field}`}
      />
    </Tooltip>
  );
};
